import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ProgressiveTypeaheadDirective } from '../../../directives/progressive-typeahead.directive';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { OpbCompanyService } from '../../../services/opb-company.service';
import { OpbCompanyCsvCheckResult, OpbCompanyCsvImportResult } from '../../../models/api/opbCompanyDto';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-opb-company-csv-import-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    ProgressiveTypeaheadDirective,
    MatTooltipModule,
    DragDropModule,
    NgxSpinnerModule
  ],
  templateUrl: './opb-company-csv-import-dialog.component.html',
  styleUrl: './opb-company-csv-import-dialog.component.scss'
})
export class OpbCompanyCsvImportDialogComponent {
  private dialogRef = inject(MatDialogRef<OpbCompanyCsvImportDialogComponent>);
  private opbCompanyService = inject(OpbCompanyService);
  private spinner = inject(NgxSpinnerService);
  private toast = inject(HotToastService);
  private confirmationDialog = inject(ConfirmationDialogService);

  selectedFile: File | null = null;
  delimiter = ',';
  checkResult: OpbCompanyCsvCheckResult | null = null;
  importResult: OpbCompanyCsvImportResult | null = null;
  isChecking = false;
  isImporting = false;
  private importedAny = false;

  readonly delimiterOptions = [
    { value: ',', label: 'Comma (,)' },
    { value: ';', label: 'Semicolon (;)' },
    { value: '\t', label: 'Tab' },
    { value: '|', label: 'Pipe (|)' }
  ];

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.toast.error('Please select a CSV file');
      input.value = '';
      return;
    }

    this.selectedFile = file;
    this.checkResult = null;
    this.importResult = null;
  }

  removeFile(): void {
    this.selectedFile = null;
    this.checkResult = null;
    this.importResult = null;
  }

  onCheck(): void {
    if (!this.selectedFile) return;

    this.isChecking = true;
    this.checkResult = null;
    this.importResult = null;
    this.spinner.show();

    this.opbCompanyService.checkCsv(this.selectedFile, this.delimiter)
      .pipe(finalize(() => {
        this.isChecking = false;
        this.spinner.hide();
      }))
      .subscribe({
        next: (result) => {
          this.checkResult = result;
          if (result.success) {
            this.toast.success('CSV validation passed');
          } else {
            this.toast.warning('CSV validation found errors');
          }
        },
        error: () => {
          this.toast.error('Failed to validate CSV file');
        }
      });
  }

  onImport(): void {
    if (!this.selectedFile) return;

    this.confirmationDialog.openConfirmationDialog(
      'Confirm Import',
      `Are you sure you want to import "${this.selectedFile.name}"?`,
      'Import',
      'Cancel',
      'cloud_upload',
      'clear'
    ).subscribe(confirmed => {
      if (!confirmed) return;
      this.executeImport();
    });
  }

  private executeImport(): void {
    this.isImporting = true;
    this.importResult = null;
    this.spinner.show();

    this.opbCompanyService.importCsv(this.selectedFile!, this.delimiter)
      .pipe(finalize(() => {
        this.isImporting = false;
        this.spinner.hide();
      }))
      .subscribe({
        next: (result) => {
          this.importResult = result;
          if (result.importedCount > 0) {
            this.importedAny = true;
          }
          if (result.success) {
            this.toast.success(`Successfully imported ${result.importedCount} companies`);
          } else {
            this.toast.warning('Import completed with errors');
          }
        },
        error: () => {
          this.toast.error('Failed to import CSV file');
        }
      });
  }

  onClose(): void {
    this.dialogRef.close(this.importedAny);
  }
}
